/**
 * Smart-link landing page for `/join/<CODE>`.
 *
 * This is the URL the admin Event tab's QR poster encodes (via
 * services/fleet.ts:publicViewerUrl). A driver / fan scanning the
 * poster with a stock camera lands here.
 *
 * Behavior:
 *   1. On mount, attempt to launch the TuviON app via the
 *      `tuvion://join/<CODE>` custom-scheme URL. If the APK is
 *      installed, Android forwards the intent to MainActivity (per
 *      Step 14.5 manifest changes), which deep-links into the PWA's
 *      `?join=<CODE>` URL and renders the JoinScreen pre-filled.
 *   2. If the app isn't installed (or the user is on iOS / desktop),
 *      the custom-scheme attempt no-ops and we show an "Install
 *      TuviON" CTA with the APK download URL. iOS / desktop users
 *      see a "Currently Android only" message.
 *
 * Production with `tuvion.tv` will eventually replace this entirely
 * via Android App Links (assetlinks.json + `https://tuvion.tv/join/*`
 * autoVerify=true intent-filter), making the smart-link page invisible
 * — Android opens the app silently before this component ever paints.
 * For staging on sslip.io (no DNS for assetlinks.json) this page is
 * the visible bridge.
 */

import React, { useEffect, useState } from 'react';
import { ExternalLink, Smartphone } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

// Where the APK is served. Deploy infrastructure must publish the
// signed APK at this path; change here OR fold into a backend config
// endpoint when prod-DNS lands.
const APK_DOWNLOAD_URL = '/apk/tuvion-latest.apk';

const CUSTOM_SCHEME_TIMEOUT_MS = 1500;

interface Props {
  code: string;
}

export const JoinLanding: React.FC<Props> = ({ code }) => {
  const [showInstallCta, setShowInstallCta] = useState(false);
  const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

  useEffect(() => {
    if (!isAndroid) {
      // iOS / desktop: skip the custom-scheme attempt, go straight to
      // the "Android only" install CTA so the user isn't confused by
      // a redirect dance that can't succeed.
      setShowInstallCta(true);
      return;
    }
    // Try to open the app. If installed, Android intercepts the
    // navigation and forwards the intent to MainActivity; the page
    // visibility flips and we never reach the timeout. If not
    // installed, the navigation no-ops and the timer fires.
    const deeplink = `tuvion://join/${encodeURIComponent(code)}`;
    const startedAt = Date.now();
    // Set immediately on mount. Synchronous so iOS-style "leave page"
    // confirmation doesn't suppress it.
    try {
      window.location.href = deeplink;
    } catch {
      // ignore — some browsers block custom schemes
    }
    const timer = window.setTimeout(() => {
      // If we got backgrounded by the app launch, we wouldn't see
      // this. Visibility-API guard avoids flashing the CTA briefly
      // before the app takes over.
      if (document.hidden) return;
      if (Date.now() - startedAt < CUSTOM_SCHEME_TIMEOUT_MS) return;
      setShowInstallCta(true);
    }, CUSTOM_SCHEME_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [code, isAndroid]);

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-4 py-6">
      <header className="mb-6 flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/15 text-accent">
          T
        </div>
        <div className="font-mono text-lg font-bold tracking-wide text-accent">TuviON</div>
      </header>

      {!showInstallCta && isAndroid ? (
        <LaunchingView />
      ) : (
        <InstallCtaView code={code} isAndroid={isAndroid} />
      )}
    </div>
  );
};

export default JoinLanding;

// ────────────────────────────────────────────────────────────────────────

const LaunchingView: React.FC = () => {
  return (
    <Card className="p-6 text-center">
      <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full border border-accent/40 bg-accent/15 text-accent">
        <Smartphone className="h-7 w-7" />
      </div>
      <h2 className="text-xl font-semibold">Opening TuviON…</h2>
      <p className="mt-2 text-sm text-fg-secondary">
        If nothing happens in a moment, you'll see install options here.
      </p>
    </Card>
  );
};

const InstallCtaView: React.FC<{ code: string; isAndroid: boolean }> = ({ code, isAndroid }) => {
  return (
    <Card className="p-6 text-center">
      <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full border border-accent/40 bg-accent/15 text-accent">
        <Smartphone className="h-7 w-7" />
      </div>
      <h2 className="text-xl font-semibold">
        {isAndroid ? 'Install TuviON to join' : 'Android required'}
      </h2>
      <p className="mt-2 text-sm text-fg-secondary">
        {isAndroid
          ? "The TuviON app isn't installed on this phone yet. Install it, then re-open this link to join the event."
          : 'TuviON is Android-only for the driver flow today. Open this link on an Android phone.'}
      </p>

      <div className="my-4 rounded-md border border-border bg-bg-input p-3 text-left">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-fg-secondary">
          Event code
        </div>
        <div className="font-mono text-2xl font-bold tracking-[0.4em] text-fg">{code}</div>
      </div>

      {isAndroid && (
        <Button asChild className="h-12 w-full gap-2 text-base">
          <a href={APK_DOWNLOAD_URL} download>
            <ExternalLink className="size-5" />
            Download TuviON APK
          </a>
        </Button>
      )}

      <p className="mt-3 text-xs text-fg-secondary">
        After install, re-scan the QR or re-open this link.
      </p>
    </Card>
  );
};
